import * as React from 'react';
import styled, { css } from 'styled-components';

import { getSemanticAttributes } from '../../internals/semanticProps';
import { rem } from '../../tokens/spacing';
import { palette } from '../../tokens/palette';

/* ═══════════════════════════════════════════════════════════════════════ */
/*  Types                                                                 */
/* ═══════════════════════════════════════════════════════════════════════ */

export type DocumentChipVariant =
  | 'default'
  | 'primary'
  | 'success'
  | 'error'
  | 'info'
  | 'muted';

export type DocumentChipSize = 'sm' | 'md' | 'lg';

export interface DocumentChipProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: DocumentChipVariant;
  size?: DocumentChipSize;
  /** Material Symbol icon name (e.g. 'draft', 'description', 'picture_as_pdf'). */
  icon?: string;
  /** Custom content to render in the icon slot instead of a Material Symbol. */
  startContent?: React.ReactNode;
  semanticId?: string;
  children: React.ReactNode;
}

/* ═══════════════════════════════════════════════════════════════════════ */
/*  Styled                                                                */
/* ═══════════════════════════════════════════════════════════════════════ */

const TRANSIENT = ['$variant', '$size', '$clickable'] as const;
const shouldForward = (prop: string | number): boolean =>
  !TRANSIENT.includes(prop as (typeof TRANSIENT)[number]);

const variantStyles = (v: DocumentChipVariant) => {
  switch (v) {
    case 'primary':
      return css`
        background-color: var(--ui-chip-bg-primary, ${palette.LightPurple});
        border-color: var(--ui-chip-bg-primary, ${palette.LightPurple});
        color: var(--ui-chip-text-primary, ${palette.Purple});
      `;
    case 'success':
      return css`
        background-color: var(--ui-chip-bg-success, ${palette.LightGreen});
        border-color: var(--ui-chip-bg-success, ${palette.LightGreen});
        color: var(--ui-chip-text-success, ${palette.Green});
      `;
    case 'error':
      return css`
        background-color: var(--ui-chip-bg-error, ${palette.LightRed});
        border-color: var(--ui-chip-bg-error, ${palette.LightRed});
        color: var(--ui-chip-text-error, ${palette.Red});
      `;
    case 'info':
      return css`
        background-color: var(--ui-chip-bg-info, ${palette.Magnolia});
        border-color: var(--ui-chip-bg-info, ${palette.Magnolia});
        color: var(--ui-chip-text-info, ${palette.Iris});
      `;
    case 'muted':
      return css`
        background-color: var(--ui-chip-bg-muted, ${palette.OffWhite});
        border-color: var(--ui-chip-bg-muted, ${palette.OffWhite});
        color: var(--ui-text, ${palette.Black});
      `;
    case 'default':
    default:
      return css`
        background-color: var(--ui-chip-bg-default, ${palette.OffWhite});
        border-color: var(--ui-chip-bg-default, ${palette.OffWhite});
        color: var(--ui-text, ${palette.Black});
      `;
  }
};

const sizeStyles = (s: DocumentChipSize) => {
  switch (s) {
    case 'sm':
      return css`
        padding: ${rem(8)} ${rem(15)};
        width: fit-content;
      `;
    case 'lg':
      return css`
        padding: ${rem(15)} ${rem(18)};
      `;
    case 'md':
    default:
      return css`
        padding: ${rem(12)};
      `;
  }
};

const StyledChip = styled.div.withConfig({ shouldForwardProp: shouldForward })<{
  $variant: DocumentChipVariant;
  $size: DocumentChipSize;
  $clickable: boolean;
}>`
  box-sizing: border-box;
  border-radius: var(--ui-radius-sm, ${rem(4)});
  border-width: ${rem(1)};
  border-style: solid;
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${rem(9)};
  flex-direction: row;
  font-family: var(--ui-font-sans);
  font-weight: 400;
  font-size: ${rem(14)};
  line-height: ${rem(20)};
  transition: border-color 200ms ease-in-out;

  ${({ $variant }) => variantStyles($variant)}
  ${({ $size }) => sizeStyles($size)}

  ${({ $clickable }) =>
    $clickable &&
    css`
      cursor: pointer;
      &:hover {
        border-color: var(--ui-interactive, ${palette.Purple});
      }
    `}

  .document-chip__icon {
    font-size: ${rem(16)};
    line-height: 1;
    flex-shrink: 0;
  }

  .document-chip__content {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    word-break: break-word;
  }
`;

/* ═══════════════════════════════════════════════════════════════════════ */
/*  Component                                                             */
/* ═══════════════════════════════════════════════════════════════════════ */

export const DocumentChip = React.forwardRef<HTMLDivElement, DocumentChipProps>(
  function DocumentChip(
    {
      variant = 'default',
      size = 'md',
      icon,
      startContent,
      semanticId,
      children,
      onClick,
      ...rest
    },
    ref,
  ) {
    const semanticProps = getSemanticAttributes(semanticId);
    const clickable = typeof onClick === 'function';

    return (
      <StyledChip
        ref={ref}
        $variant={variant}
        $size={size}
        $clickable={clickable}
        data-variant={variant}
        role={clickable ? 'button' : undefined}
        tabIndex={clickable ? 0 : undefined}
        onClick={onClick}
        onKeyDown={
          clickable
            ? (e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onClick?.(e as unknown as React.MouseEvent<HTMLDivElement>);
                }
              }
            : undefined
        }
        {...semanticProps}
        {...rest}
      >
        {(icon || startContent) && (
          <span className="document-chip__icon">
            {startContent ?? (
              <span className="material-symbols-outlined">{icon}</span>
            )}
          </span>
        )}
        <span className="document-chip__content">{children}</span>
      </StyledChip>
    );
  },
);

DocumentChip.displayName = 'DocumentChip';
